import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import probationLettersService from '../../services/probationLettersService';
import jobApplicationService from '../../services/jobApplicationService';
import NotificationToast from '../../components/ui/NotificationToast';
import apiClient from '../../lib/apiClient';

const ProbationLetters = () => {
  const [letters, setLetters] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    applicant_id: '', employee_id: '', action: 'confirm', probation_start_date: '', probation_end_date: '',
    salary: '', new_probation_end_date: '', confirmation_date: '', feedback: '', content: '', status: 'draft'
  });

  useEffect(() => {
    loadLetters();
    loadApplicants();
  }, []);

  // Cleanup blob URL when preview closes or component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadLetters = async () => {
    try {
      setLoading(true);
      const result = await probationLettersService.getProbationLetters();
      if (result?.success) setLetters(result.data || []);
    } catch (error) {
      console.error('Error loading probation letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplicants = async () => {
    try {
      const result = await jobApplicationService.getApplications({ status: 'selected' });
      if (result?.success) setApplicants(result.data || []);
    } catch (error) {
      console.error('Error loading applicants:', error);
    }
  };

  const handleSubmit = async (e, shouldClose = true) => {
    if (e) e.preventDefault();
    try {
      const result = editingLetter
        ? await probationLettersService.updateProbationLetter(editingLetter.id, formData)
        : await probationLettersService.createProbationLetter(formData);

      if (result?.success) {
        if (shouldClose) {
          setNotification({ message: `Probation letter ${editingLetter ? 'updated' : 'created'} successfully`, type: 'success' });
          setShowModal(false);
          setEditingLetter(null);
          setFormData({
            applicant_id: '', employee_id: '', action: 'confirm', probation_start_date: '', probation_end_date: '',
            salary: '', new_probation_end_date: '', confirmation_date: '', feedback: '', content: '', status: 'draft'
          });
          loadLetters();
        }
        return result.data;
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
    return null;
  };

  const handleEdit = (letter) => {
    setEditingLetter(letter);
    setFormData({
      applicant_id: letter.applicant_id || '',
      employee_id: letter.employee_id || '',
      action: letter.action || 'confirm',
      probation_start_date: letter.probation_start_date || '',
      probation_end_date: letter.probation_end_date || '',
      salary: letter.salary || '',
      new_probation_end_date: letter.new_probation_end_date || '',
      confirmation_date: letter.confirmation_date || '',
      feedback: letter.feedback || '',
      content: letter.content || '',
      status: letter.status || 'draft'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this probation letter?')) return;
    try {
      const result = await probationLettersService.deleteProbationLetter(id);
      if (result?.success) {
        setNotification({ message: 'Probation letter deleted successfully', type: 'success' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleDownload = async (id) => {
    await probationLettersService.downloadProbationLetter(id);
  };

  const handlePreview = async () => {
    if (!formData.applicant_id && !formData.employee_id) {
      setNotification({ message: 'Please select an applicant first', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      // Save (or create) the letter first to get a stable ID
      const savedData = await handleSubmit(null, false);

      if (savedData?.id) {
        const token = apiClient.getToken();
        const baseUrl = apiClient.baseURL;
        const url = `${baseUrl}/probation-letters/${savedData.id}/download?token=${token}`;

        // Fetch the HTML from the backend and show it as a blob (same as offer letter approach)
        const response = await fetch(url);
        const html = await response.text();

        // Strip source maps to avoid console noise
        const cleanHtml = html
          .replace(/\/\*# sourceMappingURL=.*?\*\//g, '')
          .replace(/\/\/# sourceMappingURL=.*?$/gm, '')
          .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

        const blob = new Blob([cleanHtml], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);

        // Revoke any previous blob URL
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(blobUrl);
        setShowPreview(true);
      } else {
        setNotification({ message: 'Failed to save letter before preview', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error generating preview', type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Probation Letters</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage employee probation periods</p>
        </div>
        <Button onClick={() => { setEditingLetter(null); setFormData({ applicant_id: '', employee_id: '', action: 'confirm', probation_start_date: '', probation_end_date: '', salary: '', new_probation_end_date: '', confirmation_date: '', feedback: '', content: '', status: 'draft' }); setShowModal(true); }}>
          <Icon name="Plus" size={16} className="mr-2" />
          Create Probation Letter
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : (
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Applicant/Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Salary (₹)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {letters.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground">
                      <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No probation letters found</p>
                      <p className="text-sm">Create your first probation letter using the button above</p>
                    </td>
                  </tr>
                ) : (
                  letters.map(letter => (
                    <tr key={letter.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">{letter.applicant_name || letter.employee_name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">{letter.applicant_email || letter.employee_email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {letter.action || 'confirm'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.probation_start_date ? new Date(letter.probation_start_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.probation_end_date ? new Date(letter.probation_end_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.salary ? `₹${Number(letter.salary).toLocaleString('en-IN')}` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${letter.status === 'draft' ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'
                          }`}>
                          {letter.status?.charAt(0).toUpperCase() + letter.status?.slice(1) || 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(letter.id)} title="Download PDF">
                            <Icon name="Download" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(letter)} title="Edit">
                            <Icon name="Edit" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(letter.id)} className="text-destructive" title="Delete">
                            <Icon name="Trash2" size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingLetter(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingLetter ? 'Edit' : 'Create'} Probation Letter</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowModal(false); setEditingLetter(null); }}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Applicant (Selected) *</label>
                  <select
                    value={formData.applicant_id}
                    onChange={(e) => setFormData({ ...formData, applicant_id: e.target.value, employee_id: '' })}
                    className="w-full px-3 py-2 border border-border rounded-md"
                    required
                  >
                    <option value="">Select Applicant</option>
                    {applicants.map(app => <option key={app.id} value={app.id}>{app.applicant_name} ({app.position})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Action *</label>
                  <select value={formData.action} onChange={(e) => setFormData({ ...formData, action: e.target.value })} className="w-full px-3 py-2 border border-border rounded-md" required>
                    <option value="confirm">Confirm (Permanent Employment)</option>
                    <option value="extend">Extend Probation</option>
                    <option value="terminate">Terminate</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Probation Start Date *" type="date" value={formData.probation_start_date} onChange={(e) => setFormData({ ...formData, probation_start_date: e.target.value })} required />
                <Input label="Probation End Date *" type="date" value={formData.probation_end_date} onChange={(e) => setFormData({ ...formData, probation_end_date: e.target.value })} required />
                <Input label="Salary (Monthly) ₹" type="number" value={formData.salary} onChange={(e) => setFormData({ ...formData, salary: e.target.value })} placeholder="e.g. 25000" />
                {formData.action === 'extend' && <Input label="New End Date" type="date" value={formData.new_probation_end_date} onChange={(e) => setFormData({ ...formData, new_probation_end_date: e.target.value })} />}
                {formData.action === 'confirm' && <Input label="Confirmation Date" type="date" value={formData.confirmation_date} onChange={(e) => setFormData({ ...formData, confirmation_date: e.target.value })} />}
              </div>
              <textarea value={formData.feedback} onChange={(e) => setFormData({ ...formData, feedback: e.target.value })} className="w-full px-3 py-2 border border-border rounded-md text-sm" rows="2" placeholder="Performance Feedback" />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Letter Content</label>
                  <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={generatingPreview || !formData.applicant_id}>
                    <Icon name="Eye" size={14} className="mr-1" />
                    {generatingPreview ? 'Saving...' : 'Preview & Print'}
                  </Button>
                </div>
                <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-3 py-2 border border-border rounded-md font-mono text-sm" rows="10" placeholder="Probation letter content..." />
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingLetter(null); }}>Cancel</Button>
                <Button type="submit">Save Letter</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPreview(false)}>
          <div className="bg-background rounded-lg p-6 max-w-4xl w-full mx-4 h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Probation Letter Preview</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <div className="flex-1 border border-border rounded-lg bg-white overflow-hidden">
              <iframe
                src={previewUrl}
                className="w-full h-full border-0"
                title="Probation Letter Preview"
                sandbox="allow-same-origin"
              />
            </div>
            <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => {
                setShowPreview(false);
                if (previewUrl && previewUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl('');
                }
              }}>Close</Button>
              <Button onClick={() => {
                const printWindow = window.open(previewUrl, '_blank');
                if (printWindow) {
                  printWindow.onload = () => {
                    setTimeout(() => printWindow.print(), 300);
                  };
                }
              }}>
                <Icon name="Printer" size={16} className="mr-2" />
                Print / Save as PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbationLetters;
